import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./command.jsx";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.jsx";
import { Button } from "./button.jsx";
import { Badge } from "./badge.jsx";
import { X as XIcon, ChevronsUpDown, Check, ChevronDown } from "lucide-react";
import { ScrollArea } from "./scroll-area.jsx";
import { cn } from "../../lib/utils.js"; // Add .js extension

const MultiSelectCreatable = ({
  options: flatOptions = [],      // Flat list of initial options (used if optionGroups not provided)
  optionGroups = [],            // Array of { label: string, options: string[] }
  selectedOptions = [],
  onChange,
  placeholder = "Select or create...",
  creatablePlaceholder = "Add new...",
  label = "Options",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); 

  // Combine all options from groups and flat options for internal management
  // This is primarily for filtering and checking existence
  const allInitialOptionsSet = useMemo(() => {
    const combined = new Set(flatOptions);
    optionGroups.forEach(group => {
      group.options.forEach(opt => combined.add(opt));
    });
    // Also add any initially selected options that might not be in the predefined lists
    // This ensures they are recognized as 'existing' when typed
    selectedOptions.forEach(opt => combined.add(opt)); 
    return combined;
  }, [flatOptions, optionGroups, selectedOptions]); // Keep selectedOptions here for checking existence

  // Initialize allOptions state once with combined initial & selected options
  const [allOptions, setAllOptions] = useState(() => {
    const combined = new Set(flatOptions);
    optionGroups.forEach(group => {
        group.options.forEach(opt => combined.add(opt));
    });
    selectedOptions.forEach(opt => combined.add(opt));
    return combined;
  });

  const handleSelect = (optionValue) => {
    const newSelectedOptions = selectedOptions.includes(optionValue)
      ? selectedOptions.filter((opt) => opt !== optionValue)
      : [...selectedOptions, optionValue];
    onChange(newSelectedOptions);
    setInputValue("");
    // Keep popover open for multi-selection by not setting open(false) here
  };

  const handleCreate = (value) => {
    const trimmedValue = value.trim();
    // Use allInitialOptionsSet for checking existence to avoid state dependency loop
    if (trimmedValue && !allInitialOptionsSet.has(trimmedValue)) { 
      setAllOptions(prev => new Set([...prev, trimmedValue])); // Add to available options state
      const newSelectedOptions = [...selectedOptions, trimmedValue];
      onChange(newSelectedOptions);
    }
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue) {
      e.preventDefault(); 
      const searchLower = inputValue.toLowerCase();
      let existingOption = null;
      // Check against the current allOptions state (which includes created ones)
      for (const option of allOptions) { 
        if (option.toLowerCase() === searchLower) {
          existingOption = option;
          break;
        }
      }

      if (existingOption && !selectedOptions.includes(existingOption)) {
        handleSelect(existingOption);
      } else if (!existingOption) {
        handleCreate(inputValue);
      }
    } else if (e.key === "Backspace" && !inputValue && selectedOptions.length > 0) {
      const newSelectedOptions = selectedOptions.slice(0, -1);
      onChange(newSelectedOptions);
    }
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    const newSelectedOptions = selectedOptions.filter((opt) => opt !== optionValue);
    onChange(newSelectedOptions);
  };

  // Toggle group expansion
  const toggleGroupExpansion = (groupLabel) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  };

  // Filtering logic needs to handle groups
  const filteredGroups = useMemo(() => {
    if (!inputValue) {
      return optionGroups;
       // Don't pre-filter options here if input is empty, 
       // let the conditional rendering handle selected check
       // .map(group => ({ ...group, options: group.options.filter(opt => !selectedOptions.includes(opt)) }))
       // .filter(group => group.options.length > 0);
    }
    const searchLower = inputValue.toLowerCase();
    return optionGroups.map(group => ({
        ...group,
        // Keep all options in the group if the group label matches, 
        // or filter options if label doesn't match
        options: group.label.toLowerCase().includes(searchLower) 
            ? group.options 
            : group.options.filter(opt => opt.toLowerCase().includes(searchLower))
      })).filter(group => group.options.length > 0); // Only show groups with matching label or options
  }, [optionGroups, inputValue]);

  // Filter flat options (only relevant if optionGroups is not used or empty)
  const filteredFlatOptions = useMemo(() => {
      if (optionGroups && optionGroups.length > 0) return []; // Prioritize groups if available
      const searchLower = inputValue.toLowerCase();
      // Filter from the current allOptions state (includes created ones)
      return Array.from(allOptions).filter( 
          (option) =>
            !selectedOptions.includes(option) &&
            option.toLowerCase().includes(searchLower)
      );
  }, [allOptions, selectedOptions, inputValue, optionGroups]);

  // Use allOptions state for checking if create option should be shown
  const showCreateOption = inputValue.trim() && !Array.from(allOptions).some(opt => opt.toLowerCase() === inputValue.trim().toLowerCase());

  // Determine if any options are available besides the create option
  const hasAvailableOptions = useMemo(() => {
      if (filteredFlatOptions.length > 0) return true;
      for (const group of filteredGroups) {
          if (group.options.some(opt => !selectedOptions.includes(opt))) {
              return true;
          }
      }
      return false;
  }, [filteredGroups, filteredFlatOptions, selectedOptions]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(`w-full justify-between h-auto min-h-[2.5rem] ${className} ${selectedOptions.length > 0 ? 'py-1 px-2' : 'py-2 px-3'}`)}
          onClick={() => setOpen(!open)}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge
                  key={option}
                  variant="secondary"
                  className="whitespace-nowrap flex items-center"
                >
                  {option}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${option}`}
                    onClick={(e) => handleRemove(option, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRemove(option, e);
                      }
                    }}
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                  >
                    <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </span>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}>
        <Command>
          <CommandInput
            ref={inputRef}
            placeholder={creatablePlaceholder}
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={handleKeyDown}
            aria-label={label}
            className="h-9 border-b"
          />
          <ScrollArea>
            <CommandList className="max-h-[300px] overflow-y-auto p-1 pb-2">
              {!hasAvailableOptions && !showCreateOption && (
                 <CommandEmpty>No options found.</CommandEmpty>
              )}
              {showCreateOption && (
                 <div 
                   className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent" 
                   onClick={() => handleCreate(inputValue)}
                   onKeyDown={(e) => { if(e.key === 'Enter') handleCreate(inputValue)}} 
                   tabIndex={0}
                   role="option"
                   aria-selected={false}
                 >
                   <span className="italic text-muted-foreground">Create "{inputValue.trim()}"</span>
                 </div>
              )}
              {filteredGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.label);
                  const visibleOptions = group.options.filter(opt => !selectedOptions.includes(opt));
                  if (visibleOptions.length === 0 && !(inputValue && group.label.toLowerCase().includes(inputValue.toLowerCase()))) {
                      return null;
                  }
                  return (
                      <CommandGroup 
                          key={group.label} 
                          heading={
                              <div 
                                  className="px-2 py-1.5 text-sm font-semibold flex items-center justify-between w-full cursor-pointer hover:bg-accent rounded-sm"
                                  onClick={() => toggleGroupExpansion(group.label)}
                              >
                                  <span>{group.label}</span>
                                  <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded ? "rotate-180" : "")} />
                              </div>
                          }
                          className="overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                      >
                          {isExpanded && visibleOptions.map((option) => (
                          <CommandItem
                              key={option}
                              value={option}
                              onSelect={() => handleSelect(option)}
                              className="pl-8 pr-2"
                          >
                              {option}
                          </CommandItem>
                          ))}
                        </CommandGroup>
                  )
              }) }
              {optionGroups.length === 0 && filteredFlatOptions.length > 0 && (
                  <CommandGroup>
                     {filteredFlatOptions.map((option) => (
                      <CommandItem
                          key={option}
                          value={option}
                          onSelect={() => handleSelect(option)}
                      >
                          {option}
                      </CommandItem>
                      ))}
                  </CommandGroup>
              )}
            </CommandList>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectCreatable; 